import type { StrategySnapshot } from "@/lib/strategy-snapshot";

export const STRATEGY_PRODUCT = {
  asin: "B0GG6YTX2H",
  title: "KASSADIN Walking Pad with Handles, Electric Treadmill for Home with Incline",
  imageUrl: "https://m.media-amazon.com/images/I/41t9KM77fbL.jpg",
  reportFileName: "B0GG6YTX2H_运营报告.pdf",
  keywordFileName: "B0GG6YTX2H_关键词筛选.xlsx",
} as const;

export const STRATEGY_KEYWORD_COLUMNS = ["搜索词", "筛选结果"] as const;
export const STRATEGY_TACTIC_IDS = [
  "builtin-14",
  "builtin-12",
  "builtin-28",
  "builtin-21",
  "builtin-26",
] as const;

// The fixed report predates live forecasting. Reuse its observed 30-day ad data if an open tab has an older snapshot cached.
const FORECAST_BASIS: NonNullable<StrategySnapshot["forecastBasis"]> = {
  periodDays: 30,
  observedFrom: "2026-08-29",
  observedTo: "2026-09-27",
  adSpend: 19215.43,
  attributedSales: 77587.11,
};

const RANK_BASIS: NonNullable<StrategySnapshot["rankBasis"]> = {
  category: "Treadmills",
  currentRank: 106,
  previousRank: 51,
  approxObservationGapDays: 14,
  capturedOn: "2026-09-30",
  sourceUrl: "https://www.amazon.com/dp/B0GG6YTX2H",
};

const FEATURE_KEYWORDS = [
  "walking pad with incline",
  "walking pad with handle bar",
  "walking pad 350+ lb capacity",
] as const;

const GOAL_FORECAST_CONTEXT: Record<
  string,
  { reallocationShare: number; limitation: string; budget: string; prerequisite?: string }
> = {
  销售额最大化: {
    reallocationShare: 0.1,
    limitation: "目标小类排名不等于预测名次；缺少策略执行后的排名响应数据。",
    budget:
      "总花费不增；从类别主题组调配至多一成现有花费给已成交功能词精准承接，观察归因销售额后再决定是否扩量。",
  },
  "广告ROAS 最大化": {
    reallocationShare: 0.05,
    limitation: "历史关键词 ACoS 不代表新增流量也能保持相同效率。",
    budget:
      "总花费不增；从类别主题组调配至多 5% 的现有花费给已成交且 ACoS 较低的功能词，先小规模验证广告投入产出比。",
  },
  利润最大化: {
    reallocationShare: 0,
    limitation: "缺少单件成本、退货损耗和库存，暂不能量化利润目标的预测结果。",
    budget:
      "先核实单件贡献利润、退货损耗与库存，再计算盈亏平衡 ACoS；核实前维持现有投放，不调配预算或承诺利润改善。",
    prerequisite: "单件贡献利润、退货损耗与库存",
  },
  排名最优化: {
    reallocationShare: 0,
    limitation:
      "缺少销量变化与小类排名之间的响应数据，目前只能按排名走势粗估，无法判断目标达成概率。",
    budget: "保持现有投放，监测小类排名与核心词成交；建立排名响应证据后再决定是否调整预算。",
    prerequisite: "销量与小类排名之间的响应证据",
  },
  评论最优化: {
    reallocationShare: 0,
    limitation: "缺少评论数量、评分及其变化数据，暂不能量化评论目标的预测结果。",
    budget: "保持现有投放；先补齐评论数量、评分与变化数据，再设计可验证的评论改善行动。",
    prerequisite: "评论数量、评分与变化数据",
  },
};

function getGoalContext(goal?: string) {
  const key = goal?.startsWith("销售额最大化") ? "销售额最大化" : goal;
  return key ? GOAL_FORECAST_CONTEXT[key] : undefined;
}

export function getProductGoalCaution(goal: string) {
  return getGoalContext(goal)?.limitation ?? "当前目标缺少可量化的策略效果数据。";
}

export function getProductForecast(snapshot: StrategySnapshot, goal: string) {
  const basis = snapshot.forecastBasis ?? FORECAST_BASIS;
  // An open wizard can retain a snapshot cached before the observation gap field was renamed.
  const cachedRankBasis = snapshot.rankBasis;
  const rankBasis =
    cachedRankBasis &&
    Number.isFinite(cachedRankBasis.currentRank) &&
    Number.isFinite(cachedRankBasis.previousRank) &&
    Number.isFinite(cachedRankBasis.approxObservationGapDays) &&
    cachedRankBasis.approxObservationGapDays > 0
      ? cachedRankBasis
      : RANK_BASIS;
  const ratio = 14 / basis.periodDays;
  const baselineSales = basis.attributedSales * ratio;
  const adSpend = basis.adSpend * ratio;
  const baselineAcos = basis.attributedSales > 0 ? basis.adSpend / basis.attributedSales : null;
  const plan = getGoalContext(goal);
  const observedFeatureAcos = FEATURE_KEYWORDS.map(
    (keyword) => snapshot.keywords.find((item) => item.keyword === keyword)?.acos,
  ).filter((acos): acos is number => typeof acos === "number" && acos > 0);
  // The highest observed ACoS among proven feature terms is the conservative target scenario.
  const targetAcos =
    observedFeatureAcos.length === FEATURE_KEYWORDS.length
      ? Math.max(...observedFeatureAcos)
      : null;
  const canModelReallocation =
    Boolean(plan?.reallocationShare) &&
    snapshot.tactics.some((tactic) => tactic.id === "builtin-01") &&
    snapshot.decision.proposedGroups.includes("功能词精准承接") &&
    baselineAcos !== null &&
    targetAcos !== null &&
    targetAcos < baselineAcos;
  const reallocatedSpend = canModelReallocation
    ? adSpend * snapshot.metrics.categorySpendShare * (plan?.reallocationShare ?? 0)
    : 0;
  // The source group's ACoS is unavailable, so use product-wide ACoS as its explicit proxy.
  const scenarioSales =
    baselineSales +
    (baselineAcos && targetAcos ? reallocatedSpend * (1 / targetAcos - 1 / baselineAcos) : 0);
  // One snapshot is the flat scenario; extending the observed two-point change is the trend scenario.
  const trendRank =
    rankBasis.currentRank +
    ((rankBasis.currentRank - rankBasis.previousRank) * 14) / rankBasis.approxObservationGapDays;
  return {
    salesRange: [baselineSales, scenarioSales] as const,
    adSpend,
    adCostShareRange:
      baselineSales > 0 ? ([adSpend / scenarioSales, adSpend / baselineSales] as const) : null,
    reallocatedSpend,
    baselineAcos,
    targetAcos,
    modeledReallocation: canModelReallocation,
    reallocationShare: plan?.reallocationShare ?? 0,
    requiresValidation: Boolean(plan?.prerequisite),
    limitation: plan?.limitation ?? "当前目标没有可量化的策略效果数据。",
    basis,
    rankRange: [
      Math.max(1, Math.floor(Math.min(rankBasis.currentRank, trendRank) / 10) * 10),
      Math.ceil(Math.max(rankBasis.currentRank, trendRank) / 10) * 10,
    ] as const,
    rankBasis,
  };
}

// The API snapshot can remain cached in an open wizard after these tactics are added.
// Keep the new product-specific copy available until that snapshot is refreshed.
const NEW_TACTIC_CONTEXT: Record<string, StrategySnapshot["tactics"][number]> = {
  "builtin-28": {
    id: "builtin-28",
    reason: "自动紧密匹配已带来成交，可继续用受控预算发现相关搜索词。",
    sections: [
      {
        title: "适用依据",
        body: "近 30 天自动紧密匹配组产生 181 单，花费约 4,684 美元，ACoS 约 25.4%。",
      },
      {
        title: "执行安排",
        body: "保留自动紧密匹配采词，从现有预算中控制探索花费；将已验证成交且规格匹配的词转入手动精准承接。",
      },
      {
        title: "审核边界",
        body: "核对近 7 天订单归因和商品规格，不因短期零订单就批量否定搜索词。",
      },
    ],
  },
  "builtin-21": {
    id: "builtin-21",
    reason: "部分搜索词已有较多点击却未见订单，适合人工复核后精准排除无效流量。",
    sections: [
      {
        title: "适用依据",
        body: "threadmills machine for home 等词出现 40 次点击、约 69 美元花费而暂无订单；400 lb 承重词的 ACoS 为 43.9%。",
      },
      {
        title: "执行安排",
        body: "按花费、点击和订单筛出低效词，核对搜索意图、商品规格及其他广告组表现后，人工确认否定精准词或降低出价。",
      },
      {
        title: "审核边界",
        body: "最近 7 天的订单可能继续回填；不得直接否定仍有成交或在其他广告组有效的词。",
      },
    ],
  },
  "builtin-26": {
    id: "builtin-26",
    reason: "当前广告 ACoS 为 24.7%，需要先核实利润与库存，才能确定安全投入边界。",
    sections: [
      {
        title: "适用依据",
        body: "商品广告 ACoS 为 24.7%，高于店内主力同类的 21.8%；成本及可售库存尚未核实。",
      },
      {
        title: "执行安排",
        body: "核实单件贡献利润和可售数量，计算盈亏平衡 ACoS；以此设定广告组出价与预算红线，再决定是否扩量。",
      },
      {
        title: "执行前提",
        body: "成本和库存未确认前，维持总花费，不把当前 ACoS 或同类均值当作利润目标。",
      },
    ],
  },
};

export function getProductTacticContext(
  snapshot: StrategySnapshot | undefined,
  id: string,
  goal?: string,
) {
  const context = snapshot?.tactics.find((tactic) => tactic.id === id);
  const selected =
    context?.reason.trim() &&
    context.sections.some((section) => section.title === "执行安排" && section.body.trim())
      ? context
      : (NEW_TACTIC_CONTEXT[id] ?? context);
  const prerequisite = getGoalContext(goal)?.prerequisite;
  if (!selected || !prerequisite) return selected;
  // These ad tactics are candidates until evidence for the selected KPI exists.
  return {
    ...selected,
    reason: `${selected.reason} 当前“${goal}”目标仍需核实${prerequisite}，此打法暂列为候选。`,
    sections: selected.sections.map((section) =>
      section.title === "执行安排"
        ? {
            title: section.title,
            body: `先核实${prerequisite}；核实前保持现有投放，不执行预算或广告组调整。验证后可考虑：${section.body}`,
          }
        : section,
    ),
  };
}

const UPDATED_DECISION = {
  title: "五项协同投放打法",
  summary: "维持总花费；SKAG、瀑布流、自动紧密匹配采词、低效词人工复核，核实利润红线后再评估扩量。",
  rationale:
    "目标商品件/会话为 6.3%，接近店内主力同类的 6.8%；广告 ACoS 为 24.7%，高于主力同类的 21.8%。以精准承接和分层管理提高效率，保留自动采词，人工复核低效词，并先核实利润与库存。",
  budget:
    "维持总花费；从类别主题组调配约一成用于已成交功能词精准承接，保留自动采词预算。低效词经人工复核后再调整，利润和库存核实前不扩量。",
};

export function getProductDecision(snapshot: StrategySnapshot, goal?: string) {
  const decision =
    snapshot.decision.title === UPDATED_DECISION.title
      ? {
          ...snapshot.decision,
          summary: snapshot.decision.summary.replace(
            "已成交功能词精准承接、广告结构分层",
            "SKAG、瀑布流",
          ),
        }
      : { ...snapshot.decision, ...UPDATED_DECISION };
  const goalContext = getGoalContext(goal);
  if (!goalContext) return decision;
  return {
    ...decision,
    title: goalContext.prerequisite ? "五项候选投放打法" : decision.title,
    summary: goalContext.prerequisite
      ? `五项投放打法列为候选；先核实${goalContext.prerequisite}，核实前保持现有投放。`
      : decision.summary,
    budget: goalContext.budget,
  };
}
